import { useEffect, useState } from 'react';
import manifest from '../../data/learnTarot/manifest.json';
import config from '../../data/learnTarot/config.json';
import { LEARN_BUNDLES } from '../../data/learnTarot/i18n';
import { CARD_BY_SLUG, HUB_PATHS, cardPath } from '../../data/cardMeanings';
import { noteHomeEvent } from '../../services/homeAnalytics';

// Production builds hide Learn Tarot until config.json says "public": true.
// Local dev can preview it with REACT_APP_LEARN_TAROT=true in .env.development.local.
export const LEARN_TAROT_ENABLED = config.public || process.env.REACT_APP_LEARN_TAROT === 'true';

export const ORIGIN = 'https://nowtarot.com';
export const HUB_PATH = '/learn-tarot/';
export const FEATURED_SLUG = 'how-to-read-tarot-cards';

export const ARTICLES = manifest.articles;
export const ARTICLE_BY_SLUG = Object.fromEntries(ARTICLES.map((a) => [a.slug, a]));

export const CATEGORY_INFO = [
  {
    id: 'getting-started',
    label: 'Getting Started',
    blurb: 'Your first reading, how the deck works, and how to ask questions that lead somewhere useful.',
    accent: 'yellow',
  },
  {
    id: 'spreads',
    label: 'Tarot Spreads',
    blurb: 'Choose a layout, understand what each position does, and read several cards as one story.',
    accent: 'pink',
  },
  {
    id: 'interpretation',
    label: 'Interpretation & Symbolism',
    blurb: 'Reversals, the Major and Minor Arcana, and using tarot as a tool for reflection.',
    accent: 'green',
  },
  {
    id: 'history',
    label: 'History & Divination',
    blurb: 'Where tarot actually came from, and how it fits among other divination traditions.',
    accent: 'yellow',
  },
];

// Loaded on demand so the hub bundle stays small.
export function loadArticle(slug) {
  if (!ARTICLE_BY_SLUG[slug]) return Promise.resolve(null);
  return import(`../../data/learnTarot/articles/${slug}.json`).then((m) => m.default || m);
}

export function noteLearnClick(kind, target, article) {
  noteHomeEvent(kind, { target, article: article || 'hub' });
}

const DATE_LOCALES = {
  en: 'en-US', es: 'es', fr: 'fr', pt: 'pt-BR', de: 'de', pl: 'pl', zh: 'zh-CN', ar: 'ar',
};

export function formatDate(iso, lang = 'en') {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(DATE_LOCALES[lang] || 'en-US', {
    year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC',
  });
}

export const LEARN_LANGS = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
  { code: 'pt', label: 'Português' },
  { code: 'de', label: 'Deutsch' },
  { code: 'pl', label: 'Polski' },
  { code: 'zh', label: '中文' },
  { code: 'ar', label: 'العربية' },
];

export const LEARN_LANG_CODES = LEARN_LANGS.map((item) => item.code).filter((code) => code !== 'en');

export function learnHubPath(lang = 'en') {
  return lang === 'en' ? HUB_PATH : `/learn-tarot/${lang}/`;
}

export function learnArticlePath(slug, lang = 'en') {
  return lang === 'en' ? `/learn-tarot/${slug}/` : `/learn-tarot/${lang}/${slug}/`;
}

export function homePath(lang = 'en') {
  return lang === 'en' ? '/' : `/${lang}/`;
}

export function meaningsHubPath(lang = 'en') {
  return HUB_PATHS[lang] || HUB_PATHS.en;
}

export function readyLearnLangs() {
  return LEARN_LANGS.filter((item) => item.code === 'en' || typeof LEARN_BUNDLES[item.code] === 'function');
}

export function loadLearnBundle(lang) {
  const load = LEARN_BUNDLES[lang];
  if (typeof load !== 'function') return Promise.resolve(null);
  return load().then((mod) => mod.default || mod);
}

export function useLearnBundle(lang) {
  const [state, setState] = useState(() => (
    !lang || lang === 'en' ? { lang: 'en', bundle: null } : { lang: '', bundle: undefined }
  ));
  useEffect(() => {
    let alive = true;
    if (!lang || lang === 'en') {
      setState({ lang: 'en', bundle: null });
      return undefined;
    }
    setState({ lang: '', bundle: undefined });
    loadLearnBundle(lang).then((bundle) => {
      if (alive) setState({ lang, bundle });
    });
    return () => { alive = false; };
  }, [lang]);
  if (!lang || lang === 'en') return null;
  return state.lang === lang ? state.bundle : undefined;
}

const SAMPLE_SLUGS = [
  'the-fool', 'the-high-priestess', 'ace-of-cups', 'three-of-pentacles', 'knight-of-wands', 'queen-of-swords',
];

export function sampleCards(lang = 'en') {
  return SAMPLE_SLUGS.map((slug) => {
    const card = CARD_BY_SLUG[slug];
    return {
      slug,
      name: card.names[lang] || card.names.en,
      image: card.image,
      to: cardPath(card, lang),
    };
  });
}

export function pageDir(lang) {
  return lang === 'ar' ? 'rtl' : 'ltr';
}
